import { computed } from '@vue/composition-api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  IWorkflowTransitionAction,
  IWorkflowTransitionActionData,
  WorkflowTransitionAction
} from '@/models/workflow';
import providerInstance from '@/services/provider';
import { User } from '@/models';

// Split into CRUD composables

export function useFetchEmailActionEmailRecipients(
  workflowId: number,
  transitionId: number
) {
  const getActionsQueryKey = () => [
    'workflow',
    workflowId,
    'transition',
    transitionId,
    'actions-email-recipients'
  ];

  const {
    data: recipients,
    isLoading: isLoadingRecipients,
    error: recipientsError,
    refetch: refetchRecipients
  } = useQuery({
    queryKey: getActionsQueryKey(),
    enabled: computed(() => !!transitionId),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowTransitionActionsEmailRecipients(
        workflowId,
        transitionId
      ),
    select: data => data.map(action => new User(action))
  });

  return {
    recipients: computed(() => recipients?.value ?? []),
    isLoadingRecipients,
    recipientsError,
    refetchRecipients
  };
}
// Fetch all actions
export function useFetchActions(workflowId: number, transitionId: number) {
  const getActionsQueryKey = () => [
    'workflow',
    workflowId,
    'transition',
    transitionId,
    'actions'
  ];

  const {
    data: actions,
    isLoading: isLoadingActions,
    error: actionsError
  } = useQuery({
    queryKey: getActionsQueryKey(),
    enabled: computed(() => !!transitionId),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowTransitionActions(
        workflowId,
        transitionId
      ),
    select: data => data.map(action => new WorkflowTransitionAction(action))
  });

  return {
    actions,
    isLoadingActions,
    actionsError
  };
}

// Fetch single action
export function useFetchAction(
  workflowId: number,
  transitionId: number,
  actionId: number
) {
  const getActionQueryKey = () => [
    'workflow',
    workflowId,
    'transition',
    transitionId,
    'actions',
    actionId
  ];

  const {
    data: action,
    isLoading: isLoadingAction,
    error: actionError
  } = useQuery({
    queryKey: getActionQueryKey(),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowTransitionAction(
        workflowId,
        transitionId,
        actionId
      ),
    enabled: !!actionId,
    select: action => new WorkflowTransitionAction(action)
  });

  return {
    action,
    isLoadingAction,
    actionError
  };
}

// Create action
export function useCreateAction(workflowId: number, transitionId: number) {
  const queryClient = useQueryClient();

  const {
    mutate: createAction,
    isLoading: isCreatingAction,
    error: createActionError,
    reset: createActionErrorReset
  } = useMutation({
    mutationFn: (data: IWorkflowTransitionActionData) =>
      providerInstance.workflow.createWorkflowTransitionAction(
        workflowId,
        transitionId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'transition',
        transitionId,
        'actions'
      ]);
    }
  });

  return {
    createAction,
    isCreatingAction,
    createActionError,
    createActionErrorReset
  };
}

// Update action
export function useUpdateAction(workflowId: number, transitionId: number) {
  const queryClient = useQueryClient();

  const {
    mutate: updateAction,
    isLoading: isUpdatingAction,
    error: updateActionError,
    reset: updateActionErrorReset
  } = useMutation({
    mutationFn: (data: IWorkflowTransitionActionData) =>
      providerInstance.workflow.updateWorkflowTransitionAction(
        workflowId,
        transitionId,
        data
      ),
    onSuccess: (_, action) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'transition',
        transitionId,
        'actions'
      ]);
      if (action.id) {
        queryClient.invalidateQueries([
          'workflow',
          workflowId,
          'transition',
          transitionId,
          'actions',
          action.id
        ]);
      }
    }
  });

  return {
    updateAction,
    isUpdatingAction,
    updateActionError,
    updateActionErrorReset
  };
}

// Delete action mutation
export function useDeleteAction(workflowId: number, transitionId: number) {
  const queryClient = useQueryClient();

  const {
    reset: deleteActionErrorReset,
    mutate: deleteAction,
    isLoading: isDeletingAction,
    error: deleteActionError
  } = useMutation({
    mutationFn: (actionId: number) =>
      providerInstance.workflow.deleteWorkflowTransitionAction(
        workflowId,
        transitionId,
        actionId
      ),
    onSuccess: (_, actionId) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'transition',
        transitionId,
        'actions'
      ]);
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'transition',
        transitionId,
        'actions',
        actionId
      ]);
    }
  });

  return {
    deleteActionErrorReset,
    deleteAction,
    isDeletingAction,
    deleteActionError
  };
}
